/*
 * LawFirmRatesCalculator
 * Calculates utilization, realization and collection rates from the reader's own numbers for one period.
 * No defaults, no benchmarks, no "good" or "bad" thresholds.
 *
 * Inputs (all optional, each rate shows when its two inputs are valid numbers greater than 0)
 *   available hours, billable hours, standard value of the billable work ($), amount billed ($), cash collected ($)
 *
 * Rules
 *   utilization %  = billable hours / available hours * 100
 *   realization %  = amount billed / standard value * 100
 *   collection %   = cash collected / amount billed * 100
 *   cash per $1 of work at standard rates = cash collected / standard value   (needs both)
 *   If realization and collection are both shown, the lower one is named as "where to look first",
 *   with the causes this post gives: realization -> pricing, scoping or write-downs;
 *   collection -> billing delays, slow payers, engagement letters or payment plans. Equal -> both.
 *   If billable hours > available hours, show a note to check the definition of available hours.
 *   Any rate above 100% gets a note that numbers may come from different periods.
 * No tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Input } from "@/components/ui/input";

type Key = "available" | "billable" | "standard" | "billed" | "collected";
type Field = { key: Key; label: string; hint: string; placeholder: string };

const FIELDS: Field[] = [
  { key: "available", label: "Available hours", hint: "Using your firm's own definition", placeholder: "e.g. 1600" },
  { key: "billable", label: "Billable hours recorded", hint: "Same period", placeholder: "e.g. 1200" },
  { key: "standard", label: "Standard value of that work ($)", hint: "Billable hours × standard rates", placeholder: "e.g. 300000" },
  { key: "billed", label: "Amount billed ($)", hint: "After write-downs", placeholder: "e.g. 270000" },
  { key: "collected", label: "Cash collected ($)", hint: "Against those bills", placeholder: "e.g. 240000" },
];

const pct = (n: number) => `${n.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function parse(raw: string): { value: number | null; error: string | null } {
  const t = raw.replace(/[$,\s]/g, "");
  if (t === "") return { value: null, error: null };
  const n = Number(t);
  if (!Number.isFinite(n)) return { value: null, error: "Enter a number." };
  if (n <= 0) return { value: null, error: "Enter a number greater than 0." };
  return { value: n, error: null };
}

export default function LawFirmRatesCalculator() {
  const uid = useId();
  const [v, setV] = useState<Record<Key, string>>({ available: "", billable: "", standard: "", billed: "", collected: "" });
  const p = Object.fromEntries(FIELDS.map((f) => [f.key, parse(v[f.key])])) as Record<Key, { value: number | null; error: string | null }>;
  const n = (k: Key) => p[k].value;

  const util = n("available") !== null && n("billable") !== null ? ((n("billable") as number) / (n("available") as number)) * 100 : null;
  const real = n("standard") !== null && n("billed") !== null ? ((n("billed") as number) / (n("standard") as number)) * 100 : null;
  const coll = n("billed") !== null && n("collected") !== null ? ((n("collected") as number) / (n("billed") as number)) * 100 : null;
  const perDollar = n("standard") !== null && n("collected") !== null ? (n("collected") as number) / (n("standard") as number) : null;

  const any = util !== null || real !== null || coll !== null;
  const over100 = [util, real, coll].some((x) => x !== null && x > 100);
  const focus =
    real !== null && coll !== null ? (real < coll ? "realization" : coll < real ? "collection" : "both") : null;

  const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Utilization, realization and collection calculator</h3>
      <p className="mt-1 text-sm text-muted-foreground">Use numbers from the same period. Fill in any pair to see that rate. Nothing is saved or sent anywhere.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => {
          const id = `${uid}-${f.key}`;
          const err = p[f.key].error;
          return (
            <div key={f.key}>
              <label htmlFor={id} className="block text-sm font-medium">{f.label}</label>
              <Input
                id={id}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder={f.placeholder}
                value={v[f.key]}
                onChange={(e) => setV((s) => ({ ...s, [f.key]: e.target.value }))}
                aria-invalid={err ? true : undefined}
                aria-describedby={`${id}-hint`}
                className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`}
              />
              <p id={`${id}-hint`} className="mt-1 text-xs text-muted-foreground">{err ?? f.hint}</p>
            </div>
          );
        })}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4 text-sm">
        {!any ? (
          <p className="text-muted-foreground">Enter at least one pair of numbers, for example available and billable hours.</p>
        ) : (
          <div>
            <dl className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-md border border-border bg-card p-3"><dt className="text-muted-foreground">Utilization</dt><dd className="text-base font-semibold">{util !== null ? pct(util) : "—"}</dd></div>
              <div className="rounded-md border border-border bg-card p-3"><dt className="text-muted-foreground">Realization</dt><dd className="text-base font-semibold">{real !== null ? pct(real) : "—"}</dd></div>
              <div className="rounded-md border border-border bg-card p-3"><dt className="text-muted-foreground">Collection</dt><dd className="text-base font-semibold">{coll !== null ? pct(coll) : "—"}</dd></div>
            </dl>
            {perDollar !== null && (
              <p className="mt-3">
                For every $1 of work at standard rates, the firm collected <strong>{usd(perDollar)}</strong>.
              </p>
            )}
            {focus === "realization" && (
              <p className="mt-3">Look at realization first. A gap here usually points to pricing, scoping or write-downs.</p>
            )}
            {focus === "collection" && (
              <p className="mt-3">Look at collection first. A gap here can come from billing delays, slow payers, weak engagement letters or payment plans.</p>
            )}
            {focus === "both" && <p className="mt-3">Realization and collection are level, so check both.</p>}
            {util !== null && util > 100 && (
              <p className="mt-3 text-muted-foreground">Billable hours are higher than available hours. Check how your firm defines available hours.</p>
            )}
            {over100 && (
              <p className="mt-3 text-muted-foreground">A rate above 100% often means the numbers come from different periods.</p>
            )}
            <p className="mt-3">
              Want your marketing measured the same way?{" "}
              <Link to="/services/law-firm-marketing-analytics" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                See our law firm marketing analytics →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
